import {
  HuaweiDownloadButton,
  LinkButton,
  HeroV2,
  Button,
  Action,
  CopyButton,
  ButtonV2,
} from '@hg/stylizator/components'
import { TButtonColor, TButtonVariant } from '@hg/stylizator/dist/esm/components/atoms/ButtonV2/models'
import { Text } from '@hg/stylizator/typography'
import { desktopLarge, Flex, isUndefined, media, Spacing, themeColors } from '@hg/stylizator/utils'
import * as React from 'react'
import { mobileVendor, mobileModel } from 'react-device-detect'
import styled from 'styled-components'

import { ThemeTypeData } from 'app/blocks/sections/Hero/models'
import ClientOnly from 'app/components/ClientOnly'
import OsDownloadButton from 'app/components/OSDownloadButton'
import Android from 'app/icons/platform/Android'
import Apple from 'app/icons/platform/Apple'
import Docker from 'app/icons/platform/Docker'
import Macos from 'app/icons/platform/Macos'
import Windows from 'app/icons/platform/Windows'

export interface IDownloadPlatform {
  platform: string
  icon: React.ReactElement
  link: string
  enabled: boolean
}

export interface IHeroAttributes
  extends Omit<React.ComponentProps<typeof HeroV2>, 'leftButton' | 'rightButton' | 'illustrationUrl'> {
  rightButtonText?: string
  rightButtonUrl?: string
  rightButtonClassName?: string
  illustration: string
  leftButtonText: string
  leftButtonUrl: string
  leftButtonClassName?: string
  videoUrl?: string
  leftButtonOpenInNewTab?: boolean
  leftButtonWindowsText?: string
  leftButtonMacosText?: string
  leftButtonWindowsUrl?: string
  leftButtonWindows7Url?: string
  leftButtonMacosUrl?: string
  leftButtonAndroidText?: string
  leftButtonAndroidUrl?: string
  leftButtoniOSText?: string
  leftButtoniOSUrl?: string
  leftButtonDockerText?: string
  leftButtonDockerUrl?: string
  addDeviceModelTargeting?: boolean
  appGalleryDownloadUrl?: string
  androidDownloadUrl?: string
  infinixDownloadUrl?: string
  isDownloadEnabled?: boolean
  osButtonBlockData?: any
  showStatistics: boolean
  statisticsWithIcons: boolean
  fullHeight: boolean
  displayLogo: boolean
  downloads?: IDownloadPlatform[]
  subheadingText: React.ReactNode

  headingText: string
  headingMobileText: string
  themedHeadingText: string
  themedHeadingMobileText: string

  displayCoupon: boolean
  afterCopyText: string
  couponCode: string
  couponRightButtonTitle: string
  couponRightButtonLink: string
  couponSublabel: string
  couponButtonClassName: string
  actionButtonClassName: string

  imageMobileId: number
  imageMobileUrl: string
  showMobileImage: boolean
}

export const getDownloadsList = (attr: IHeroAttributes): IDownloadPlatform[] => [
  {
    platform: 'Windows',
    icon: <Windows />,
    link: attr.leftButtonWindowsUrl ?? '',
    enabled: attr.leftButtonWindowsUrl !== '' && !isUndefined(attr.leftButtonWindowsUrl),
  },
  {
    platform: 'Android',
    icon: <Android />,
    link: attr.leftButtonAndroidUrl ?? '',
    enabled: attr.leftButtonAndroidUrl !== '' && !isUndefined(attr.leftButtonAndroidUrl),
  },
  {
    platform: 'MacOS',
    icon: <Macos />,
    link: attr.leftButtonMacosUrl ?? '',
    enabled: attr.leftButtonMacosUrl !== '' && !isUndefined(attr.leftButtonMacosUrl),
  },
  {
    platform: 'iOS',
    icon: <Apple />,
    link: attr.leftButtoniOSUrl ?? '',
    enabled: attr.leftButtoniOSUrl !== '' && !isUndefined(attr.leftButtoniOSUrl),
  },
  {
    platform: 'Docker',
    icon: <Docker />,
    link: attr.leftButtonDockerUrl ?? '',
    enabled: attr.leftButtonDockerUrl !== '' && !isUndefined(attr.leftButtonDockerUrl),
  },
]

const HeroBlock: React.FC<IHeroAttributes> = (props) => {
  const [videoOpened, setVideoOpened] = React.useState<boolean>(false)

  const theme = props.theme || 'default'
  const isHuawei = mobileVendor.toLowerCase() === 'huawei'
  const isInfinix = mobileVendor.toLowerCase() === 'infinix' || mobileModel.toLowerCase().includes('infinix')
  const statistics =
    props.statistics &&
    props.statistics.map(({ heading, description, iconUrl }) => {
      return props.statisticsWithIcons
        ? {
            description,
            iconUrl,
          }
        : {
            heading,
            description,
          }
    })

  const couponVariant: Record<
    IHeroAttributes['theme'],
    {
      variant: Extract<TButtonVariant, 'copy' | 'copySpecial'>
      sublabelColor: 'dark' | 'light'
      buttonVariant: Extract<TButtonColor, 'primary' | 'accent'>
    }
  > = {
    default: {
      variant: 'copy',
      sublabelColor: 'dark',
      buttonVariant: 'primary',
    },
    blue: {
      variant: 'copySpecial',
      sublabelColor: 'light',
      buttonVariant: 'accent',
    },
    dark: {
      variant: 'copySpecial',
      sublabelColor: 'light',
      buttonVariant: 'accent',
    },
    cbMintgreen: {
      variant: 'copy',
      sublabelColor: 'dark',
      buttonVariant: 'primary',
    },
    cbSpecial: {
      variant: 'copySpecial',
      sublabelColor: 'light',
      buttonVariant: 'accent',
    },
    outerSpace: {
      variant: 'copy',
      sublabelColor: 'dark',
      buttonVariant: 'accent',
    },
  }

  const renderLeftButton = () => {
    if (props.addDeviceModelTargeting && isHuawei) {
      return (
        <HuaweiDownloadButton
          className={props.leftButtonClassName || 'track-hero-huawei-download-button homepage-hero-left-button'}
          href={props.appGalleryDownloadUrl}
          fullWidth={{ default: true, desktop: false }}
        />
      )
    }

    if (props.isDownloadEnabled) {
      return (
        <ClientOnly>
          <OsDownloadButton
            leftButtonWindowsText={props.leftButtonWindowsText ?? ''}
            leftButtonMacosText={props.leftButtonMacosText ?? ''}
            leftButtonAndroidText={props.leftButtonAndroidText ?? ''}
            leftButtonWindowsUrl={props.leftButtonWindowsUrl ?? ''}
            leftButtonWindows7Url={props.leftButtonWindows7Url ?? ''}
            leftButtonMacosUrl={props.leftButtonMacosUrl ?? ''}
            leftButtonAndroidUrl={props.leftButtonAndroidUrl ?? ''}
            leftButtoniOSText={props.leftButtoniOSText ?? ''}
            leftButtoniOSUrl={props.leftButtoniOSUrl ?? ''}
            skipUnknown
            fullWidth={{ default: true, desktop: false }}
            specialTheme={ThemeTypeData[theme].specialTheme}
            className={props.leftButtonClassName || 'homepage-hero-left-button'}
          />
        </ClientOnly>
      )
    }

    if (props.leftButtonText === 'none' ? undefined : props.leftButtonText && props.leftButtonUrl) {
      return (
        <LinkButton
          className={props.leftButtonClassName || 'track-hero-left-button homepage-hero-left-button'}
          type={ThemeTypeData[theme].leftButtonStyle}
          useCompact
          fullWidth={{ default: true, desktop: false }}
          href={props.leftButtonUrl}
          target={props.leftButtonOpenInNewTab ? '_blank' : undefined}
        >
          {props.leftButtonText}
        </LinkButton>
      )
    }

    return (
      <ClientOnly>
        <OsDownloadButton
          fullWidth={{ default: true, desktop: false }}
          specialTheme={ThemeTypeData[theme].specialTheme}
          {...(props.osButtonBlockData?.attrs ?? {})}
          className={`track-hero-download homepage-hero-left-button ${
            isInfinix ? 'track-hero-infinix-download-button' : ''
          } ${props.osButtonBlockData?.attrs?.className ?? ''} ${props.leftButtonClassName}`}
          leftButtonAndroidUrl={
            isInfinix ? props.infinixDownloadUrl : props.osButtonBlockData?.attrs?.leftButtonAndroidUrl
          }
        />
      </ClientOnly>
    )
  }

  const renderRightButton = () => {
    if (props.addDeviceModelTargeting && isHuawei) {
      return (
        <LinkButton
          className="track-hero-android-download-button"
          type={ThemeTypeData[theme].rightButtonStyle}
          useCompact
          href={props.androidDownloadUrl}
          fullWidth={{ default: true, desktop: false }}
        >
          Download app files
        </LinkButton>
      )
    }

    return props.rightButtonText ? (
      <LinkButton
        className={`track-hero-right-button ${props.rightButtonClassName}`}
        type={ThemeTypeData[theme].rightButtonStyle}
        useCompact
        href={props.rightButtonUrl}
        fullWidth={{ default: true, desktop: false }}
      >
        {props.rightButtonText}
      </LinkButton>
    ) : undefined
  }

  const renderDownloadLinks = () => {
    if (props.isDownloadEnabled) {
      return (
        <DownloadsContainer>
          <Spacing top={[1, 3]} bottom={1}>
            <Text type="body">
              <DownloadText>Download Carebuzz on:</DownloadText>
            </Text>
          </Spacing>
          <DownloadButtonsContainer>
            {props.downloads?.map((it) => {
              const button = (
                <Button
                  type="secondary"
                  key={it.platform}
                  className={!it.enabled ? 'disabled' : ''}
                  inactive={!it.enabled}
                  disabled={!it.enabled}
                >
                  <Flex flexDir="column" align="center" justify="center">
                    {it.icon}
                    <div style={{ wordWrap: 'normal' }}>
                      <Text type="subtitle" color={themeColors.blue[60]} fontWeight={it.enabled ? 700 : 400}>
                        <span>{it.enabled ? it.platform : 'coming soon'}</span>
                      </Text>
                    </div>
                  </Flex>
                </Button>
              )

              return it.enabled ? (
                <a href={it.link} target="_blank" key={it.platform} rel="noreferrer">
                  {button}
                </a>
              ) : (
                button
              )
            })}
          </DownloadButtonsContainer>
        </DownloadsContainer>
      )
    }
    return null
  }

  const renderCouponLinks = () => {
    if (props.displayCoupon) {
      return (
        <Action
          align={{ default: 'flex-start', desktop: 'flex-start', mobile: 'center' }}
          reverseOnMobile
          sublabel={props.couponSublabel}
          sublabelColor={couponVariant[theme].sublabelColor}
          leftButton={
            props.couponCode ? (
              <ButtonContainer>
                <CopyButton
                  copyText={props.afterCopyText || props.couponCode}
                  variant={couponVariant[theme].variant}
                  buttonProps={{
                    icon: 'copy',
                    className: props.couponButtonClassName,
                  }}
                >
                  {props.couponCode}
                </CopyButton>
              </ButtonContainer>
            ) : null
          }
          rightButton={
            props.couponRightButtonTitle ? (
              <ButtonContainer>
                <ButtonV2
                  as="a"
                  className={props.rightButtonClassName}
                  variant="primary"
                  color={ThemeTypeData[theme].specialTheme ? 'accent' : 'primary'}
                  size="medium"
                  href={props.couponRightButtonLink}
                >
                  {props.couponRightButtonTitle}
                </ButtonV2>
              </ButtonContainer>
            ) : null
          }
        />
      )
    }
    return null
  }

  return (
    <HeroV2
      className={`homepage-hero-section ${props.className}`}
      theme={theme}
      headingText={props.headingText}
      themedHeadingText={props.themedHeadingText}
      headingMobileText={props.headingMobileText}
      themedHeadingMobileText={props.themedHeadingMobileText}
      subheadingText={props.subheadingText}
      illustrationUrl={props.illustration}
      illustrationAlt={props.illustrationAlt}
      leftButton={renderLeftButton()}
      rightButton={renderRightButton()}
      contentBelowButtons={renderDownloadLinks()}
      leftRightButtonsOverride={renderCouponLinks()}
      videoUrl={props.videoUrl}
      videoOpened={videoOpened}
      onVideoPlayClick={() => setVideoOpened(true)}
      onVideoCloseClick={() => setVideoOpened(false)}
      statistics={props.showStatistics ? statistics : undefined}
      showTrustpilot={props.showTrustpilot}
      fullHeight={props.fullHeight}
      imageMobileUrl={props.imageMobileUrl}
      showMobileImage={props.showMobileImage}
    />
  )
}

export default HeroBlock

const ButtonContainer = styled.div`
  width: auto;
  & > * {
    min-width: 190px;
  }

  @media ${media.mobile} {
    width: 100%;
  }
`

const DownloadsContainer = styled.div`
  margin-top: 16px;

  & button {
    width: auto;
    height: auto;
    min-width: 88px;
    min-height: 53px;
    padding: 4px 8px;
    color: ${themeColors.blue[60]};
  }

  & button.disabled {
    background: ${themeColors.blue[20]};
    opacity: 0.2;
  }
`

const DownloadText = styled.span`
  text-align: center;

  @media ${desktopLarge} {
    text-align: left;
  }
`

const DownloadButtonsContainer = styled(Flex)`
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;

  @media ${desktopLarge} {
    justify-content: flex-start;
  }
`
